import React, { useState } from 'react';
import { ImageAnalysis } from '../types';
import { Hash, Briefcase, MessageCircle, Image, Maximize2, FileText } from 'lucide-react';

interface Props {
  imageAnalysis: ImageAnalysis;
  onOpenImage?: () => void;
}

export default function AnalysisSummary({ imageAnalysis, onOpenImage }: Props) {
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const formattedAnalysis = imageAnalysis.analysis.replace(/\.\s+/g, '.\n\n');
  const isTextAnalysis = !imageAnalysis.imageUrl && 'text' in imageAnalysis;

  return (
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Post Context</h2>

      {/* Key Details */}
      <div className="bg-white/80 rounded-xl p-6 mb-6">
        <h3 className="font-semibold text-gray-700 mb-4">Key Details</h3>
        <div className="flex justify-between items-start">
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <Hash className="w-4 h-4 text-blue-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm text-gray-500">Topic</div>
                <div className="text-gray-900 line-clamp-2 break-words">{imageAnalysis.context.topic}</div>
              </div>
            </div>
          </div>
          
          <div className="flex-1 min-w-0 mx-8">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                <Briefcase className="w-4 h-4 text-purple-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm text-gray-500">Industry</div>
                <div className="text-gray-900 line-clamp-2 break-words">{imageAnalysis.context.industry}</div>
              </div>
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-rose-600" />
              </div>
              <div className="min-w-0">
                <div className="text-sm text-gray-500">Tone</div>
                <div className="text-gray-900 line-clamp-2 break-words">{imageAnalysis.context.tone}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-2 gap-6">
        {/* Left Column - Image or Text */}
        <div className="bg-white/80 rounded-xl p-6">
          <h3 className="font-semibold text-gray-700 mb-4">Original Post</h3>
          {isTextAnalysis ? (
            <div className="h-[400px] overflow-y-auto">
              <div className={`prose text-gray-600 ${!isTextExpanded ? 'line-clamp-6' : ''}`}>
                <p className="whitespace-pre-wrap">{imageAnalysis.text}</p>
              </div>
              <button
                onClick={() => setIsTextExpanded(!isTextExpanded)}
                className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                {isTextExpanded ? 'Show less' : 'Show more'}
              </button>
            </div>
          ) : imageAnalysis.imageUrl ? (
            <div className="relative">
              <button 
                onClick={onOpenImage}
                className="group relative w-full"
              >
                <div className="relative h-[400px] w-full">
                  <img 
                    src={imageAnalysis.imageUrl} 
                    alt="Analyzed post" 
                    className="absolute inset-0 w-full h-full object-contain bg-gray-50 rounded-lg"
                  />
                </div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/10 rounded-lg">
                  <Maximize2 className="w-8 h-8 text-white" />
                </div>
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[400px] bg-gray-50 rounded-lg">
              <Image className="w-8 h-8 text-gray-400" />
            </div>
          )}
        </div>

        {/* Right Column - Summary */}
        <div className="bg-white/80 rounded-xl p-6">
          <h3 className="font-semibold text-gray-700 mb-3">Analysis Summary</h3>
          <p className="text-gray-600 whitespace-pre-line">
            {formattedAnalysis}
          </p>
        </div>
      </div>
    </div>
  );
}
